import type { ReportSchema } from '@ballerine/common';
import { NOT_DETECTED } from '@ballerine/common';
import type { LucideIcon } from 'lucide-react';
import { Building, Check, Copy, Earth, Hash, Mail, MapPin, Phone } from 'lucide-react';
import React, { useState } from 'react';
import type z from 'zod';

import { ctw } from '@/common';
import { TextWithNAFallback } from '@/components/atoms';

type DFMerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'];
  className?: string;
};

const CopyButton = React.memo<{ textToCopy: string; className?: string }>(
  ({ textToCopy, className }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard?.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    };

    return (
      <button
        onClick={handleCopy}
        className={ctw('text-gray-400 hover:text-gray-600 transition-colors', className)}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      </button>
    );
  },
);

CopyButton.displayName = 'CopyButton';

const DataRow: React.FC<{
  label: string;
  Icon: LucideIcon;
  value: React.ReactNode;
  copyable?: boolean;
  copyValue?: string;
}> = ({ label, Icon, value, copyable = false, copyValue }) => {
  if (!value) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500" />
        <p className="text-sm text-slate-400 mb-1">{label}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-sm text-gray-900 truncate">{value}</div>
        {copyable && copyValue && <CopyButton textToCopy={copyValue} />}
      </div>
      {/* <a href="#" className="ml-4" target="_blank" rel="noopener noreferrer"> */}
      {/*   <ExternalLink className="h-3 w-3 text-blue-600 hover:text-blue-400 transition-colors" /> */}
      {/* </a> */}
    </div>
  );
};

export const DFMerchantDetailsContent: React.FC<DFMerchantDetailsContentProps> = ({
  merchantDetails,
  className,
}) => {
  return (
    <div className={ctw(className)}>
      <DataRow
        label="Company Name"
        Icon={Building}
        value={<TextWithNAFallback>{merchantDetails?.companyName}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.companyName || NOT_DETECTED}
      />

      <DataRow
        label="Registration Number"
        Icon={Hash}
        value={<TextWithNAFallback>{merchantDetails?.registrationNumber}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.registrationNumber || NOT_DETECTED}
      />

      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={<TextWithNAFallback>{merchantDetails?.phoneNumber}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.phoneNumber || NOT_DETECTED}
      />

      <DataRow
        label="Email"
        Icon={Mail}
        value={<TextWithNAFallback>{merchantDetails?.email}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.email || NOT_DETECTED}
      />

      <DataRow
        label="Country"
        Icon={Earth}
        value={<TextWithNAFallback>{merchantDetails?.country}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.country || NOT_DETECTED}
      />

      <DataRow
        label="Address"
        Icon={MapPin}
        value={<TextWithNAFallback>{merchantDetails?.address}</TextWithNAFallback>}
        copyable
        copyValue={merchantDetails?.address || NOT_DETECTED}
      />
    </div>
  );
};
